'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { replaceVariablesWithSampleData } from '@/lib/phishing/personalization-variables';
import { applyPreviewSrc } from '@/lib/brand-logos/preview-substitution';
import { resolvePayloadPreviewTokens } from '@/lib/phishing/preview-payload-tokens';
import { decodeCfEmailObfuscation } from '@/lib/phishing/decode-cf-email';
import { useTranslation } from '@/lib/i18n';
import { useInertLinksHtml } from '@/lib/email-preview/inert-links-html';

/**
 * Renders HTML inside `<iframe srcdoc>` with `sandbox` for full
 * isolation. This is what react-email (Resend) and the GoDaddy
 * `react-safe-src-doc-iframe` library do — the iframe sandbox is the
 * security boundary, not a sanitizer.
 *
 * **Why no DOMPurify?**
 * 1. The `sandbox="allow-same-origin"` attribute blocks scripts,
 *    forms, top-navigation, popups — everything DOMPurify guards
 *    against. The browser enforces it natively.
 * 2. DOMPurify silently strips `<style>` tags positioned first in the
 *    document (cure53/DOMPurify#804, #257), which our awareness
 *    templates always have right after `<!DOCTYPE><html><head>`.
 * 3. DOMPurify's CSS sanitizer also drops complex selectors,
 *    gradients, `calc()`, `@keyframes`, and `@media` queries that
 *    brand templates depend on.
 * 4. Templates come from authenticated admins via the BE library —
 *    same trust level as our own code. They're already going to send
 *    this exact HTML to real recipients in a phishing email.
 *
 * **What the sandbox blocks:**
 * - `<script>` execution
 * - `<form>` submission
 * - `window.top` navigation
 * - Plugin embed/popup
 * - Pointer-lock + automatic-download
 *
 * Without `allow-scripts` in the sandbox token list, our autosize
 * postMessage can't run inside the iframe — so we measure height
 * via the iframe's `contentDocument.body.scrollHeight` after `load`
 * (cross-document read works because we keep `allow-same-origin`).
 */

export function IsolatedPreview({
  html,
  className,
  style,
  brandDomain,
  skipSubstitution,
  autoResize = true,
  minHeight = 200,
  attachmentFileName,
}: {
  html: string;
  className?: string;
  style?: React.CSSProperties;
  /** Scenario/brand root (e.g. `amazon.com`) — lets `{{logoUrl}}` resolve
   * to the matching brand logo instead of the generic HF default. Callers
   * with scenario context should pass
   * `brandDomainFromLandingUrl(scenario.landingBaseUrl)`. */
  brandDomain?: string;
  /** Opt-out for callers that intentionally want to display raw tokens
   * (editor raw-HTML views, etc.). Defaults to false so every preview
   * surface automatically substitutes `{{firstName}}`, `{{logoUrl}}`,
   * `{{company}}`, etc. without each caller having to remember. */
  skipSubstitution?: boolean;
  /** When true (default) the iframe resizes to match its content height.
   * Set false for scaled-thumbnail callers that want a fixed viewport. */
  autoResize?: boolean;
  /** Fallback min-height before measurement and the floor after sizing. */
  minHeight?: number;
  /** The template's configured attachment file name, so body copy that reads
   * "{{attachmentFileName}}" names the real file. Omitted → a neutral sample
   * (STG-762; resolvePayloadPreviewTokens). */
  attachmentFileName?: string;
}) {
  const { t } = useTranslation();
  // A PREVIEW must never navigate. Without this, clicking a CTA navigated the
  // FRAME itself (the sandbox only stops it escaping to the top window): the
  // target loaded with no `allow-scripts` and the preview went blank — QA on
  // the PhishTrainer inbox, 2026-09-01 — and an invite email's CTA is the
  // learner's single-use magic link, so an admin previewing it could REDEEM
  // somebody else's invite. Until 2026-10-06 this was `a { pointer-events:
  // none }`, which left Enter on a focused link working and hid where a link
  // pointed. Now every link loses its href before the frame gets it, and
  // hovering names its address (lib/email-preview/inert-links-html.ts).
  const inertLinks = useInertLinksHtml();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);

  // Build the srcdoc: substitute tokens (sample data for preview) and
  // inject a tiny color-scheme reset so admin dark-mode doesn't bleed
  // into preview chrome. Template's own CSS wins via cascade.
  const srcdoc = useMemo(() => {
    // Two substitution layers: mustache tokens ({{firstName}}, {{logoUrl}})
    // + <img data-brand-key> -> absolute resolve-preview URL. Both are
    // idempotent + safe to run against already-rendered HTML, so every
    // preview surface (grid, lightbox, review, editor preview tab) gets
    // accurate brand logos without each caller needing to remember.
    // Resolve send-time payload tokens ({{qrCode}}, {{attachmentBlock}}, the
    // legacy [QR CODE PLACEHOLDER]) into temporary preview visuals so the
    // editor shows a sample QR / attachment chip where the real per-recipient
    // asset is injected at send (2026-05-29).
    // Cloudflare's email-obfuscation placeholder decodes via a script at
    // /cdn-cgi/…, which a srcdoc iframe cannot resolve — so "[email protected]"
    // would stay on screen where the sender address belongs. Runs before
    // everything else and is a no-op on markup without the marker.
    const deobfuscated = decodeCfEmailObfuscation(html);
    const withTokens = resolvePayloadPreviewTokens(
      skipSubstitution
        ? deobfuscated
        : replaceVariablesWithSampleData(
            deobfuscated,
            brandDomain ? { brandDomain } : undefined,
          ),
      { attachmentFileName },
    );
    const resolved = skipSubstitution ? withTokens : applyPreviewSrc(withTokens);

    // Light-scheme reset injected into <head> if present, otherwise
    // wrapped around a fragment. Forces white background + dark text
    // so dark-mode admins still see the email rendered as recipients
    // would see it (Gmail/Outlook ignore admin dark-mode preferences).
    const reset = `<style data-hf-preview-reset>
      :root { color-scheme: light; }
      html, body { background: #ffffff; color: #111111; }
      /* Never let an image spill past the preview frame — the same way email
         clients cap images to the viewport width. Fixes the "broken UI" where
         an uncapped template image overflowed the frame. */
      img { max-width: 100%; height: auto; }
      /* Brand logos are served as large (~1024px) square PNGs; a template that
         didn't size its logo (e.g. an AI-generated email) would render it as a
         giant block. Cap runaway logos with max-* ONLY — no \`width\`/\`height\`
         override — so well-sized templates (the seed emails set width:120px)
         are untouched while giants shrink to a real email-header size.
         \`data-brand-key\` is what applyPreviewSrc tags; object-fit keeps aspect. */
      img[data-brand-key], .brand-logo img, img.brand-logo {
        max-height: 64px; max-width: 100%; object-fit: contain;
      }
    </style>`;

    if (/<head\b/i.test(resolved)) {
      return resolved.replace(/<head\b[^>]*>/i, (m) => `${m}${reset}`);
    }
    if (/<body\b/i.test(resolved)) {
      return `<!DOCTYPE html><html><head>${reset}</head>${resolved.replace(/<\/?html[^>]*>/gi, '')}</html>`;
    }
    // Fragment — wrap in minimal document shell.
    return `<!DOCTYPE html><html><head>${reset}</head><body>${resolved}</body></html>`;
  }, [html, brandDomain, skipSubstitution, attachmentFileName]);

  // Measure content height after load so the iframe expands to match
  // (no inner scrollbars). We can read contentDocument because we keep
  // `allow-same-origin` in the sandbox; without that the cross-doc
  // read would throw. We re-measure on every srcdoc change.
  useEffect(() => {
    if (!autoResize) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const h = Math.max(
          doc.documentElement.scrollHeight,
          doc.body?.scrollHeight ?? 0,
        );
        if (h > 0) setMeasuredHeight(Math.max(h, minHeight));
      } catch {
        // Cross-origin or sandbox change — ignore, fall back to minHeight.
      }
    };

    const onLoad = () => {
      // Run measurements at multiple ticks so async assets (images,
      // webfonts) that change layout get re-measured as they paint.
      measure();
      setTimeout(measure, 100);
      setTimeout(measure, 500);
      setTimeout(measure, 1500);
    };

    iframe.addEventListener('load', onLoad);
    // Also measure once if iframe is already loaded (HMR re-mount).
    if (iframe.contentDocument?.readyState === 'complete') onLoad();

    return () => {
      cancelled = true;
      iframe.removeEventListener('load', onLoad);
    };
  }, [srcdoc, autoResize, minHeight]);

  const frameStyle: React.CSSProperties = {
    width: '100%',
    border: 'none',
    display: 'block',
    background: '#ffffff',
    colorScheme: 'light',
    height: autoResize ? (measuredHeight ?? minHeight) : undefined,
    minHeight: autoResize ? undefined : minHeight,
    ...style,
  };

  return (
    <iframe
      ref={iframeRef}
      className={className}
      style={frameStyle}
      srcDoc={inertLinks(srcdoc)}
      /* `allow-same-origin` lets us read contentDocument for autosize.
         No `allow-scripts` — script execution is blocked entirely.
         No `allow-forms` / `allow-top-navigation` — preview can't
         submit forms or escape the embed. */
      sandbox="allow-same-origin"
      title={t('phishingUi.emails.preview')}
    />
  );
}
